import {
  Download,
  Eye,
  Loader2,
  RefreshCw,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { startTransition, useEffect, useMemo, useRef, useState } from "react";
import {
  type LifecycleStage,
  StatusBadge,
} from "@/components/status/StatusBadge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  getRunHistoryOutcomeSummary,
  RUN_HISTORY_PAGE_SIZE,
  type RunHistoryKind,
  type RunHistoryQuery,
  type RunHistoryStatus,
  toKindLabel,
  type UnifiedRunHistoryItem,
  type UnifiedRunStatus,
} from "./status-domain";

type RunHistorySectionProps = {
  runs: UnifiedRunHistoryItem[];
  totalRuns: number;
  pageCount: number;
  query: RunHistoryQuery;
  refreshingRuns: boolean;
  downloadingPdfRunId: number | null;
  activeRun: UnifiedRunStatus | null;
  hasActiveRuns: boolean;
  onQueryChange: (
    updater: (prev: RunHistoryQuery) => RunHistoryQuery,
    historyMode?: "push" | "replace",
  ) => void;
  onRefreshRuns: () => Promise<void>;
  onSelectRun: (
    runId: number,
    kind: UnifiedRunHistoryItem["kind"],
  ) => Promise<void>;
  onDeleteRun: (
    runId: number,
    kind: UnifiedRunHistoryItem["kind"],
  ) => Promise<void>;
  onDownloadRunPdf: (
    runId: number,
    kind: UnifiedRunHistoryItem["kind"],
  ) => Promise<void>;
  onBeginRunSelection: (
    runId: number,
    kind: UnifiedRunHistoryItem["kind"],
  ) => void;
  isRunPending: (runId: number, kind: UnifiedRunHistoryItem["kind"]) => boolean;
  onAfterSelectRun: () => void;
};

const dateTimeFormatter = new Intl.DateTimeFormat("it-IT", {
  dateStyle: "short",
  timeStyle: "short",
});

const integerFormatter = new Intl.NumberFormat("it-IT");

function toLifecycleStage(
  status: UnifiedRunHistoryItem["status"],
): LifecycleStage {
  if (status === "queued") return "queued";
  if (status === "processing") return "processing";
  if (status === "completed") return "completed";
  if (status === "failed") return "failed";
  return "uploaded";
}

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return "—";
  return dateTimeFormatter.format(parsed);
}

function hasActiveFilters(query: RunHistoryQuery): boolean {
  return (
    query.kind !== "all" ||
    query.status !== "all" ||
    query.fileQuery.trim().length > 0 ||
    query.page !== 1
  );
}

function renderPaginationSummary(totalRuns: number, page: number): string {
  if (totalRuns === 0) {
    return "Nessuna analisi";
  }

  const start = (page - 1) * RUN_HISTORY_PAGE_SIZE + 1;
  const end = Math.min(totalRuns, page * RUN_HISTORY_PAGE_SIZE);
  return `${integerFormatter.format(start)}-${integerFormatter.format(end)} di ${integerFormatter.format(totalRuns)} analisi`;
}

function getDeletePrompt(item: UnifiedRunHistoryItem): {
  title: string;
  description: string;
  confirmLabel: string;
  successMessage: string;
} {
  const filename = item.inputFilename?.trim() || `elemento #${item.id}`;

  return {
    title:
      item.kind === "fatture_ddt"
        ? "Rimuovere questa analisi?"
        : "Rimuovere questa polizza?",
    description: `L'elemento ${filename} verra eliminato in modo permanente dallo storico. Questa azione non puo essere annullata.`,
    confirmLabel:
      item.kind === "fatture_ddt" ? "Elimina analisi" : "Elimina polizza",
    successMessage:
      item.kind === "fatture_ddt"
        ? `Analisi ${filename} rimossa dallo storico.`
        : `Polizza ${filename} rimossa dallo storico.`,
  };
}

export function RunHistorySection({
  runs,
  totalRuns,
  pageCount,
  query,
  refreshingRuns,
  downloadingPdfRunId,
  activeRun,
  hasActiveRuns,
  onQueryChange,
  onRefreshRuns,
  onSelectRun,
  onDeleteRun,
  onDownloadRunPdf,
  onBeginRunSelection,
  isRunPending,
  onAfterSelectRun,
}: RunHistorySectionProps) {
  const [fileInputValue, setFileInputValue] = useState(query.fileQuery);
  const [confirmDeleteTarget, setConfirmDeleteTarget] =
    useState<UnifiedRunHistoryItem | null>(null);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(
    null,
  );
  const [deleteFeedback, setDeleteFeedback] = useState<string | null>(null);
  const [deletingRunKey, setDeletingRunKey] = useState<string | null>(null);
  const deleteDialogRef = useRef<HTMLDialogElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const FILE_FILTER_DEBOUNCE_MS = 250;

  useEffect(() => {
    setFileInputValue(query.fileQuery);
  }, [query.fileQuery]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      if (fileInputValue === query.fileQuery) {
        return;
      }

      onQueryChange(
        (prev) => ({
          ...prev,
          fileQuery: fileInputValue,
          page: 1,
        }),
        "replace",
      );
    }, FILE_FILTER_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [fileInputValue, onQueryChange, query.fileQuery]);

  useEffect(() => {
    if (query.page <= pageCount) {
      return;
    }

    onQueryChange(
      (prev) => ({
        ...prev,
        page: Math.max(1, pageCount),
      }),
      "replace",
    );
  }, [onQueryChange, pageCount, query.page]);

  useEffect(() => {
    const dialog = deleteDialogRef.current;
    if (!dialog) return;

    if (confirmDeleteTarget) {
      if (!dialog.open) {
        dialog.showModal();
      }
      return;
    }

    if (dialog.open) {
      dialog.close();
    }
  }, [confirmDeleteTarget]);

  useEffect(() => {
    if (hasActiveRuns && confirmDeleteTarget) {
      setConfirmDeleteTarget(null);
      setDeleteErrorMessage(null);
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
    }
  }, [confirmDeleteTarget, hasActiveRuns]);

  const activeRunHiddenByFilters = useMemo(() => {
    if (!activeRun) return false;
    return !runs.some(
      (item) => item.id === activeRun.id && item.kind === activeRun.kind,
    );
  }, [activeRun, runs]);

  const emptyMessage = hasActiveFilters(query)
    ? "Nessuna analisi trovata con i filtri correnti."
    : "Nessuna analisi disponibile nello storico.";

  function handleResetFilters() {
    startTransition(() => {
      setFileInputValue("");
      onQueryChange(
        () => ({
          kind: "all",
          status: "all",
          fileQuery: "",
          page: 1,
        }),
        "replace",
      );
    });
  }

  async function handleOpenRun(
    runId: number,
    kind: UnifiedRunHistoryItem["kind"],
  ) {
    onBeginRunSelection(runId, kind);
    await onSelectRun(runId, kind);
    onAfterSelectRun();
  }

  function handleRequestDelete(
    item: UnifiedRunHistoryItem,
    triggerElement?: HTMLElement | null,
  ) {
    if (hasActiveRuns) return;
    setDeleteFeedback(null);
    setDeleteErrorMessage(null);
    returnFocusRef.current = triggerElement ?? null;
    setConfirmDeleteTarget(item);
  }

  function finalizeDeleteDialogClose() {
    setConfirmDeleteTarget(null);
    setDeleteErrorMessage(null);
    returnFocusRef.current?.focus();
    returnFocusRef.current = null;
  }

  function closeDeleteDialog() {
    const dialog = deleteDialogRef.current;
    if (dialog?.open) {
      dialog.close();
      return;
    }

    finalizeDeleteDialogClose();
  }

  async function handleConfirmDelete() {
    if (!confirmDeleteTarget) return;

    const runKey = `${confirmDeleteTarget.kind}:${confirmDeleteTarget.id}`;
    const prompt = getDeletePrompt(confirmDeleteTarget);

    try {
      setDeleteErrorMessage(null);
      setDeletingRunKey(runKey);
      await onDeleteRun(confirmDeleteTarget.id, confirmDeleteTarget.kind);
      setDeleteFeedback(prompt.successMessage);
      closeDeleteDialog();
    } catch (error) {
      const message =
        error instanceof Error && error.message.trim().length > 0
          ? error.message
          : "Impossibile completare la rimozione.";
      setDeleteErrorMessage(message);
    } finally {
      setDeletingRunKey(null);
    }
  }

  const deletePrompt = confirmDeleteTarget
    ? getDeletePrompt(confirmDeleteTarget)
    : null;

  return (
    <section
      className="status-panel__card status-history"
      aria-label="Storico analisi"
    >
      <div className="status-history__header">
        <div className="status-history__heading">
          <h3 className="status-panel__section-title">Storico analisi</h3>
          <p className="status-history__summary">
            {renderPaginationSummary(totalRuns, query.page)}
          </p>
        </div>
        <div className="status-history__header-actions">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="status-history__refresh"
            onClick={() => void onRefreshRuns()}
            disabled={refreshingRuns}
            aria-label="Aggiorna storico analisi"
          >
            <RefreshCw
              size={14}
              aria-hidden="true"
              className={cn(
                "status-panel__runs-refresh-icon",
                refreshingRuns ? "status-panel__runs-refresh-icon--spin" : "",
              )}
            />
            Aggiorna
          </Button>
        </div>
      </div>

      <fieldset className="status-history__filters">
        <legend className="status-history__legend">Filtri storico</legend>
        <label className="status-history__filter status-history__filter--search">
          <span className="upload-form__label">Nome file</span>
          <input
            className="upload-form__input"
            type="text"
            name="runFilename"
            autoComplete="off"
            spellCheck={false}
            value={fileInputValue}
            onChange={(event) => setFileInputValue(event.target.value)}
          />
        </label>

        <label className="status-history__filter">
          <span className="upload-form__label">Tipo</span>
          <select
            className="upload-form__input"
            name="runKind"
            value={query.kind}
            onChange={(event) => {
              const nextKind = event.target.value as RunHistoryKind;
              onQueryChange(
                (prev) => ({
                  ...prev,
                  kind: nextKind,
                  page: 1,
                }),
                "replace",
              );
            }}
          >
            <option value="all">Tutti</option>
            <option value="fatture_ddt">Fatture + DDT</option>
            <option value="polizza_carico">Polizza di carico</option>
          </select>
        </label>

        <label className="status-history__filter">
          <span className="upload-form__label">Stato</span>
          <select
            className="upload-form__input"
            name="runStatus"
            value={query.status}
            onChange={(event) => {
              const nextStatus = event.target.value as RunHistoryStatus;
              onQueryChange(
                (prev) => ({
                  ...prev,
                  status: nextStatus,
                  page: 1,
                }),
                "replace",
              );
            }}
          >
            <option value="all">Tutti</option>
            <option value="queued">In coda</option>
            <option value="processing">In lavorazione</option>
            <option value="completed">Completato</option>
            <option value="failed">Fallito</option>
          </select>
        </label>

        <div className="status-history__filter status-history__filter--actions">
          <span className="upload-form__label">Azioni</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="status-history__reset"
            onClick={handleResetFilters}
            disabled={!hasActiveFilters(query)}
          >
            <RotateCcw size={14} aria-hidden="true" />
            Resetta filtri
          </Button>
        </div>
      </fieldset>

      {activeRunHiddenByFilters ? (
        <p className="status-history__helper">
          L&apos;analisi aperta non rientra nei filtri correnti. Resetta i
          filtri per ritrovarla nello storico.
        </p>
      ) : null}

      {hasActiveRuns ? (
        <p className="status-history__helper status-history__helper--warning">
          La rimozione è temporaneamente bloccata mentre il sistema sta
          elaborando un documento.
        </p>
      ) : null}

      {deleteFeedback ? (
        <p className="status-history__feedback" aria-live="polite">
          {deleteFeedback}
        </p>
      ) : null}

      {runs.length === 0 ? (
        <p className="status-history__empty">{emptyMessage}</p>
      ) : (
        <>
          <div className="status-history__table-wrap">
            <table className="status-history__table">
              <thead>
                <tr>
                  <th>Nome file</th>
                  <th>Stato</th>
                  <th>Avvio</th>
                  <th>Fine</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((item) => {
                  const isActive =
                    activeRun?.id === item.id && activeRun.kind === item.kind;
                  const isDownloadingRowPdf = downloadingPdfRunId === item.id;
                  const isPending = isRunPending(item.id, item.kind);
                  const isDeleting =
                    deletingRunKey === `${item.kind}:${item.id}`;
                  const outcome = getRunHistoryOutcomeSummary(item);

                  return (
                    <tr
                      key={`${item.kind}:${item.id}`}
                      className={cn(
                        "status-history__row",
                        isActive ? "status-history__row--active" : "",
                      )}
                    >
                      <td
                        className="status-history__file"
                        title={item.inputFilename ?? "n.d."}
                      >
                        <span className="status-history__file-name">
                          {item.inputFilename ?? "n.d."}
                        </span>
                        <span className="status-history__file-meta">
                          Analisi #{integerFormatter.format(item.id)} ·{" "}
                          {toKindLabel(item.kind)}
                          {item.renderDpiUsed
                            ? ` · ${item.renderDpiUsed} DPI`
                            : ""}
                        </span>
                      </td>
                      <td>
                        <div className="status-history__state">
                          <StatusBadge
                            stage={toLifecycleStage(item.status)}
                            tone={outcome.badgeTone}
                            compact
                          />
                          <span className="status-history__outcome-label">
                            {outcome.label}
                          </span>
                          {outcome.detail ? (
                            <span className="status-history__outcome-detail">
                              {outcome.detail}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td>{formatDateTime(item.startedAt)}</td>
                      <td>{formatDateTime(item.finishedAt)}</td>
                      <td>
                        <div className="status-history__actions">
                          {item.status === "completed" &&
                          item.kind === "fatture_ddt" ? (
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              className="status-history__icon-action"
                              disabled={isDownloadingRowPdf}
                              aria-label={`Scarica report PDF di ${item.inputFilename ?? `analisi ${item.id}`}`}
                              onClick={() =>
                                void onDownloadRunPdf(item.id, item.kind)
                              }
                            >
                              <Download size={14} aria-hidden="true" />
                            </Button>
                          ) : null}
                          <Button
                            type="button"
                            aria-label={`Apri dettagli analisi ${item.id}`}
                            size="icon"
                            variant={isActive ? "default" : "outline"}
                            className="status-history__icon-action"
                            disabled={isPending}
                            onClick={() =>
                              void handleOpenRun(item.id, item.kind)
                            }
                          >
                            {isPending ? (
                              <Loader2
                                size={14}
                                aria-hidden="true"
                                className="status-history__action-spinner"
                              />
                            ) : (
                              <Eye size={14} aria-hidden="true" />
                            )}
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            className="status-history__icon-action status-history__icon-action--danger"
                            disabled={isPending || isDeleting || hasActiveRuns}
                            aria-label={`Rimuovi ${item.kind === "fatture_ddt" ? "analisi" : "polizza"} ${item.id}`}
                            onClick={(event) =>
                              handleRequestDelete(item, event.currentTarget)
                            }
                          >
                            {isDeleting ? (
                              <Loader2
                                size={14}
                                aria-hidden="true"
                                className="status-history__action-spinner"
                              />
                            ) : (
                              <Trash2 size={14} aria-hidden="true" />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="status-history__mobile-list">
            {runs.map((item) => {
              const isActive =
                activeRun?.id === item.id && activeRun.kind === item.kind;
              const isDownloadingRowPdf = downloadingPdfRunId === item.id;
              const isPending = isRunPending(item.id, item.kind);
              const isDeleting = deletingRunKey === `${item.kind}:${item.id}`;
              const outcome = getRunHistoryOutcomeSummary(item);

              return (
                <article
                  key={`mobile-${item.kind}:${item.id}`}
                  className={cn(
                    "status-history__mobile-card",
                    isActive ? "status-history__mobile-card--active" : "",
                  )}
                >
                  <div className="status-history__mobile-head">
                    <div>
                      <strong className="status-history__mobile-title">
                        Analisi #{integerFormatter.format(item.id)}
                      </strong>
                      <p className="status-history__mobile-subtitle">
                        {toKindLabel(item.kind)}
                        {item.renderDpiUsed
                          ? ` · ${item.renderDpiUsed} DPI`
                          : ""}
                      </p>
                    </div>
                    <StatusBadge
                      stage={toLifecycleStage(item.status)}
                      tone={outcome.badgeTone}
                    />
                  </div>
                  <dl className="status-history__mobile-grid">
                    <div>
                      <dt>File</dt>
                      <dd>{item.inputFilename ?? "n.d."}</dd>
                    </div>
                    <div>
                      <dt>Tipo</dt>
                      <dd>{toKindLabel(item.kind)}</dd>
                    </div>
                    <div>
                      <dt>Esito</dt>
                      <dd>
                        {outcome.label}
                        {outcome.detail ? ` · ${outcome.detail}` : ""}
                      </dd>
                    </div>
                    <div>
                      <dt>Avvio</dt>
                      <dd>{formatDateTime(item.startedAt)}</dd>
                    </div>
                    <div>
                      <dt>Fine</dt>
                      <dd>{formatDateTime(item.finishedAt)}</dd>
                    </div>
                  </dl>
                  <div className="status-history__mobile-actions">
                    {item.status === "completed" &&
                    item.kind === "fatture_ddt" ? (
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="status-history__icon-action"
                        disabled={isDownloadingRowPdf}
                        aria-label={`Scarica report PDF di ${item.inputFilename ?? `analisi ${item.id}`}`}
                        onClick={() =>
                          void onDownloadRunPdf(item.id, item.kind)
                        }
                      >
                        <Download size={14} aria-hidden="true" />
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      aria-label={`Apri analisi ${item.id}`}
                      size="sm"
                      variant={isActive ? "default" : "outline"}
                      disabled={isPending}
                      onClick={() => void handleOpenRun(item.id, item.kind)}
                    >
                      {isPending
                        ? "Apro…"
                        : isActive
                          ? "Aperta"
                          : "Apri dettagli"}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="status-history__delete-mobile"
                      disabled={isPending || isDeleting || hasActiveRuns}
                      onClick={(event) =>
                        handleRequestDelete(item, event.currentTarget)
                      }
                    >
                      {isDeleting ? (
                        <>
                          <Loader2
                            size={14}
                            aria-hidden="true"
                            className="status-history__action-spinner"
                          />
                          Rimuovo…
                        </>
                      ) : (
                        <>
                          <Trash2 size={14} aria-hidden="true" />
                          Rimuovi
                        </>
                      )}
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}

      <div className="status-history__footer">
        <div className="status-history__pagination">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              onQueryChange(
                (prev) => ({
                  ...prev,
                  page: Math.max(1, prev.page - 1),
                }),
                "push",
              )
            }
            disabled={query.page <= 1}
          >
            Precedente
          </Button>
          <span className="status-history__page">
            Pagina {integerFormatter.format(query.page)} di{" "}
            {integerFormatter.format(pageCount)}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              onQueryChange(
                (prev) => ({
                  ...prev,
                  page: Math.min(pageCount, prev.page + 1),
                }),
                "push",
              )
            }
            disabled={query.page >= pageCount}
          >
            Successiva
          </Button>
        </div>
      </div>

      <dialog
        ref={deleteDialogRef}
        className="status-history__dialog"
        aria-labelledby="status-history-delete-title"
        aria-describedby="status-history-delete-description"
        onClose={finalizeDeleteDialogClose}
        onCancel={(event) => {
          if (deletingRunKey !== null) {
            event.preventDefault();
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape" && deletingRunKey !== null) {
            event.preventDefault();
          }
        }}
        onClick={(event) => {
          if (event.target === event.currentTarget && deletingRunKey === null) {
            closeDeleteDialog();
          }
        }}
      >
        <div className="status-history__dialog-panel">
          <p className="status-history__dialog-eyebrow">Rimozione permanente</p>
          <h4
            id="status-history-delete-title"
            className="status-history__dialog-title"
          >
            {deletePrompt?.title ?? "Rimuovi elemento"}
          </h4>
          <p
            id="status-history-delete-description"
            className="status-history__dialog-description"
          >
            {deletePrompt?.description ??
              "Questa azione elimina definitivamente l'elemento selezionato."}
          </p>
          {deleteErrorMessage ? (
            <p className="status-history__dialog-error" role="alert">
              {deleteErrorMessage}
            </p>
          ) : null}
          <div className="status-history__dialog-actions">
            <Button
              type="button"
              variant="outline"
              onClick={closeDeleteDialog}
              disabled={deletingRunKey !== null}
            >
              Annulla
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleConfirmDelete()}
              disabled={deletingRunKey !== null}
            >
              {deletingRunKey !== null ? (
                <>
                  <Loader2
                    size={14}
                    aria-hidden="true"
                    className="status-history__action-spinner"
                  />
                  Rimozione…
                </>
              ) : (
                (deletePrompt?.confirmLabel ?? "Elimina")
              )}
            </Button>
          </div>
        </div>
      </dialog>
    </section>
  );
}
